/**
 * AssignProvidersPanel — Azure "Add members"-style right slide-in flyout for the
 * Clients→providers direction: lists assignable providers, multi-select, and an
 * optional "Caseload Owner" toggle in the Selected section.
 *
 * Two-pane body: AVAILABLE (search + results) on the left, SELECTED on the right —
 * each pane scrolls independently so a long results list never pushes Selected out
 * of view. This is a fixed side pane, so per CLAUDE.md's two-panel-layout rule it
 * stacks into a single scrolling column (as before) at media.desktop (≤1024), not
 * media.tablet — landscape tablets are exactly where a fixed side panel like this
 * one breaks (at 1024px the panel is a full 880px wide with an unrushed ~430/430
 * split; it only nears its 95vw cap below ~926px).
 *
 * The owner toggle is single-select and is disabled when the client already has a
 * caseload owner (the change-owner / Red Dot transfer flow is a later slice).
 */

import { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Check, Search, MapPin, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useAssignableProviders, useAssignProvidersToClient } from '../../hooks/useClients';
import type { AssignableProvider } from '../../services/api/clientService';
import { formatPersonMeta, matchesPerson } from '../shared/PersonMeta';
import { media } from '../../styles/media';

interface AssignProvidersPanelProps {
  clientId: string;
  clientName?: string;
  onClose: () => void;
  onAssigned?: () => void;
  /**
   * Override the panel z-index (and backdrop = zIndex - 1).
   * Raise this when the panel is opened from inside another overlay (e.g. ClientDetailDrawer)
   * so it stacks above it. Defaults to theme.zIndex.modal (1050).
   */
  zIndex?: number;
}

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const Overlay = styled.div<{ $zIndex?: number }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${({ $zIndex, theme }) => ($zIndex != null ? $zIndex - 1 : theme.zIndex.modalBackdrop)};
`;

const Panel = styled.aside<{ $zIndex?: number }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(880px, 95vw);
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: ${({ $zIndex, theme }) => $zIndex ?? theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} ${({ theme }) => theme.transitions.base} ease-out;
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const HeaderInfo = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

/**
 * Two panes side by side, each scrolling independently so the panel itself never
 * grows. Stacks into a single scrolling column at media.desktop, per CLAUDE.md's
 * fixed-side-pane rule (see file header) — landscape tablets are where a 50/50
 * split inside this panel would get cramped.
 */
const TwoPaneBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;

  ${media.desktop} {
    flex-direction: column;
    overflow-y: auto;
  }
`;

const Pane = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  overflow-y: auto;

  ${media.desktop} {
    flex: none;
    overflow-y: visible;
  }
`;

const AvailablePane = styled(Pane)`
  border-right: 1px solid ${({ theme }) => theme.colors.borders.light};

  ${media.desktop} {
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }
`;

const SearchBox = styled.div`
  position: relative;
  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.light};
  }
  input {
    width: 100%;
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
    padding-left: 34px;
    border: 1px solid ${({ theme }) => theme.colors.borders.medium};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const SectionHeading = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Row = styled.button<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary.base : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $selected }) =>
    $selected
      ? (theme.colors.primary.lightSky ?? theme.colors.backgrounds.light)
      : theme.colors.backgrounds.white};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  &:hover {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? theme.colors.borders.light : theme.colors.primary.base};
  }
`;

const CheckBox = styled.span<{ $checked?: boolean }>`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1.5px solid
    ${({ theme, $checked }) => ($checked ? theme.colors.primary.base : theme.colors.borders.medium)};
  background: ${({ theme, $checked }) => ($checked ? theme.colors.primary.base : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  svg {
    width: 12px;
    height: 12px;
  }
`;

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  .meta {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.text.secondary};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    /* "Location · Role" can be long — wrap instead of widening the row. */
    overflow-wrap: anywhere;
    svg {
      flex-shrink: 0;
      width: 11px;
      height: 11px;
    }
  }
`;

const AssignedTag = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const OwnerToggle = styled.button<{ $on?: boolean; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid
    ${({ theme, $on }) => ($on ? theme.colors.primary.base : theme.colors.borders.medium)};
  background: ${({ theme, $on }) => ($on ? theme.colors.primary.base : 'transparent')};
  color: ${({ theme, $on }) => ($on ? '#fff' : theme.colors.text.secondary)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  svg {
    width: 12px;
    height: 12px;
  }
`;

const EmptyNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const Note = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: ${({ theme }) => theme.spacing[1]} 0 0;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

const StateBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const fullName = (p: AssignableProvider) => `${p.firstName} ${p.lastName}`.trim();

export function AssignProvidersPanel({
  clientId,
  clientName,
  onClose,
  onAssigned,
  zIndex,
}: AssignProvidersPanelProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useAssignableProviders(clientId, true);
  const assignMutation = useAssignProvidersToClient(clientId);

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [ownerMemberId, setOwnerMemberId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentOwner = data?.currentCaseloadOwner ?? null;
  const ownerLocked = !!currentOwner;

  const providers = useMemo(() => data?.providers ?? [], [data]);

  // Matches name, email, location/center and role (ADO 398 + 406).
  const filtered = useMemo(
    () => providers.filter((p) => matchesPerson(p, search)),
    [providers, search],
  );

  const selected = useMemo(
    () => providers.filter((p) => selectedIds.includes(p.memberId)),
    [providers, selectedIds],
  );

  const toggleSelect = (p: AssignableProvider) => {
    if (p.alreadyAssigned) return;
    setSelectedIds((prev) =>
      prev.includes(p.memberId) ? prev.filter((id) => id !== p.memberId) : [...prev, p.memberId],
    );
    // Dropping a provider also clears it as owner.
    if (ownerMemberId === p.memberId) setOwnerMemberId(null);
  };

  const toggleOwner = (memberId: string) => {
    if (ownerLocked) return;
    setOwnerMemberId((prev) => (prev === memberId ? null : memberId));
  };

  const handleAssign = () => {
    if (selectedIds.length === 0) return;
    setError(null);
    assignMutation.mutate(
      {
        memberIds: selectedIds,
        caseloadOwnerMemberId: ownerMemberId ?? undefined,
      },
      {
        onSuccess: () => {
          onAssigned?.();
          onClose();
        },
        onError: (err: unknown) => {
          const status = (err as { response?: { status?: number } })?.response?.status;
          setError(
            status === 409 ? t('assignProviders.ownerConflict') : t('assignProviders.assignError'),
          );
        },
      },
    );
  };

  return (
    <Overlay onClick={onClose} $zIndex={zIndex}>
      <Panel onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" $zIndex={zIndex}>
        <Header>
          <HeaderInfo>
            <Title>{t('assignProviders.title')}</Title>
            {clientName && (
              <Subtitle>{t('assignProviders.subtitle', { client: clientName })}</Subtitle>
            )}
          </HeaderInfo>
          <CloseBtn onClick={onClose} aria-label={t('assignProviders.cancel')}>
            <X />
          </CloseBtn>
        </Header>

        <TwoPaneBody>
          {/* Available — search + results */}
          <AvailablePane>
            <SearchBox>
              <Search />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('assignProviders.searchPlaceholder')}
              />
            </SearchBox>

            <div>
              <SectionHeading>{t('assignProviders.resultsHeading')}</SectionHeading>
              {isLoading && (
                <StateBox>
                  <LoadingSpinner size="sm" /> {t('assignProviders.loading')}
                </StateBox>
              )}
              {isError && !isLoading && <ErrorText>{t('assignProviders.errorLoading')}</ErrorText>}
              {!isLoading && !isError && filtered.length === 0 && (
                <EmptyNote>{t('assignProviders.noResults')}</EmptyNote>
              )}
              <List>
                {filtered.map((p) => {
                  const checked = p.alreadyAssigned || selectedIds.includes(p.memberId);
                  return (
                    <Row
                      key={p.memberId}
                      type="button"
                      $selected={selectedIds.includes(p.memberId)}
                      $disabled={p.alreadyAssigned}
                      disabled={p.alreadyAssigned}
                      onClick={() => toggleSelect(p)}
                    >
                      <CheckBox $checked={checked}>{checked && <Check />}</CheckBox>
                      <RowInfo>
                        <div className="name">{fullName(p)}</div>
                        <div className="meta">
                          <MapPin />{' '}
                          {formatPersonMeta({
                            centerName: p.centerName,
                            roleName: p.roleName,
                            locationFallback: t('assignProviders.locationUnknown'),
                          })}
                        </div>
                      </RowInfo>
                      {p.alreadyAssigned && (
                        <AssignedTag>{t('assignProviders.assignedBadge')}</AssignedTag>
                      )}
                    </Row>
                  );
                })}
              </List>
            </div>
          </AvailablePane>

          {/* Selected */}
          <Pane>
            <SectionHeading>
              {t('assignProviders.selectedHeading', { count: selected.length })}
            </SectionHeading>
            {selected.length === 0 ? (
              <EmptyNote>{t('assignProviders.noneSelected')}</EmptyNote>
            ) : (
              <List>
                {selected.map((p) => {
                  const isOwner = ownerMemberId === p.memberId;
                  return (
                    <Row key={p.memberId} as="div" $selected>
                      <RowInfo>
                        <div className="name">{fullName(p)}</div>
                        <div className="meta">
                          <MapPin />{' '}
                          {formatPersonMeta({
                            centerName: p.centerName,
                            roleName: p.roleName,
                            locationFallback: t('assignProviders.locationUnknown'),
                          })}
                        </div>
                      </RowInfo>
                      <OwnerToggle
                        type="button"
                        $on={isOwner}
                        $disabled={ownerLocked}
                        disabled={ownerLocked}
                        title={
                          ownerLocked
                            ? t('assignProviders.ownerLockedNote', {
                                name: `${currentOwner!.firstName} ${currentOwner!.lastName}`,
                              })
                            : t('assignProviders.caseloadOwner')
                        }
                        onClick={() => toggleOwner(p.memberId)}
                      >
                        <Crown /> {t('assignProviders.caseloadOwner')}
                      </OwnerToggle>
                    </Row>
                  );
                })}
              </List>
            )}
            {ownerLocked && (
              <Note>
                {t('assignProviders.ownerLockedNote', {
                  name: `${currentOwner!.firstName} ${currentOwner!.lastName}`,
                })}
              </Note>
            )}
          </Pane>
        </TwoPaneBody>

        <Footer>
          <Subtitle>
            {currentOwner
              ? t('assignProviders.currentOwner', {
                  name: `${currentOwner.firstName} ${currentOwner.lastName}`,
                })
              : t('assignProviders.noOwner')}
          </Subtitle>
          {error && <ErrorText>{error}</ErrorText>}
          <FooterActions>
            <Button variant="ghost" onClick={onClose}>
              {t('assignProviders.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={selectedIds.length === 0}
              isLoading={assignMutation.isPending}
              onClick={handleAssign}
            >
              {selectedIds.length > 0
                ? t('assignProviders.assignCount', { count: selectedIds.length })
                : t('assignProviders.assign')}
            </Button>
          </FooterActions>
        </Footer>
      </Panel>
    </Overlay>
  );
}

export default AssignProvidersPanel;
